import { useCallback, useEffect, useState } from "react"
import type { MutableRefObject } from "react"
import AnnotationService from "services/annotations.service"
import type { TCoordinates } from "types/elevationReferences.type"
import type { TViewerPane } from "services/annotations.service"
import type { TPaneBinding } from "../core/viewerPanes"
import type { TViewerTool } from "../core/viewerTools"

const DRAG_THRESHOLD_PX = 4
const MIN_AREA_SIZE_PX = 6

export type TAreaDraft = {
	pageNumber: number
	coordinates: TCoordinates
	pane: TViewerPane
}

export type TAreaCreateSession = { area: TAreaDraft | null }

type TClientRect = { left: number; top: number; right: number; bottom: number }

export type TAreaCreateContext = {
	createSessionRef: MutableRefObject<TAreaCreateSession | null>
	draftRectRef: MutableRefObject<{ element: HTMLElement; handles: HTMLElement[] } | null>
	appContainerRef: MutableRefObject<HTMLElement | null>
	getViewerPanes: () => TPaneBinding[]
	layoutEpoch: number
	onToolChange: (tool: TViewerTool) => void
	clearSelectionToolbar: () => void
}

/** Owns the shared draw-and-resize rectangle interaction used by manual detections. */
export const useAreaCreate = (ctx: TAreaCreateContext) => {
	const {
		createSessionRef,
		draftRectRef,
		appContainerRef,
		getViewerPanes,
		layoutEpoch,
		onToolChange,
		clearSelectionToolbar,
	} = ctx
	const [createSession, setCreateSession] = useState<TAreaCreateSession | null>(null)

	const hideDraftRect = useCallback(() => {
		const element = draftRectRef.current?.element
		if (element) element.style.display = "none"
	}, [draftRectRef])

	const showDraftRect = useCallback(
		(rect: TClientRect, appRect: { left: number; top: number }) => {
			const element = draftRectRef.current?.element
			if (!element) return
			element.style.left = `${rect.left - appRect.left}px`
			element.style.top = `${rect.top - appRect.top}px`
			element.style.width = `${rect.right - rect.left}px`
			element.style.height = `${rect.bottom - rect.top}px`
			element.style.display = "block"
		},
		[draftRectRef],
	)

	const syncDraftRect = useCallback(() => {
		const area = createSessionRef.current?.area
		if (!area) {
			hideDraftRect()
			return
		}
		const rect = AnnotationService.getClientRectFromBackendRect(area.pageNumber, area.coordinates, area.pane)
		if (!rect) {
			hideDraftRect()
			return
		}
		const appRect = appContainerRef.current?.getBoundingClientRect()
		showDraftRect(rect, { left: appRect?.left ?? 0, top: appRect?.top ?? 0 })
	}, [appContainerRef, createSessionRef, hideDraftRect, showDraftRect])

	const setArea = useCallback(
		(area: TAreaDraft | null) => {
			if (!createSessionRef.current) return
			const next = { area }
			createSessionRef.current = next
			setCreateSession(next)
			syncDraftRect()
		},
		[createSessionRef, syncDraftRect],
	)

	const cancelCreateArea = useCallback((): boolean => {
		if (!createSessionRef.current) return false
		createSessionRef.current = null
		setCreateSession(null)
		hideDraftRect()
		AnnotationService.endAreaDrawing()
		return true
	}, [createSessionRef, hideDraftRect])

	const startCreateArea = useCallback(() => {
		AnnotationService.deselectAll()
		clearSelectionToolbar()
		onToolChange("select")
		AnnotationService.beginAreaDrawing()
		const session: TAreaCreateSession = { area: null }
		createSessionRef.current = session
		setCreateSession(session)
		hideDraftRect()
	}, [clearSelectionToolbar, createSessionRef, hideDraftRect, onToolChange])

	useEffect(() => {
		if (!createSession) return
		const panes = getViewerPanes()
		if (panes.length === 0) return

		let start: { x: number; y: number } | null = null
		let pane: TViewerPane = "primary"
		let isDragging = false
		let anchor: { x: number; y: number } | null = null
		let appRect = { left: 0, top: 0 }
		let latestRect: TClientRect | null = null

		const measureAppRect = () => {
			const measured = appContainerRef.current?.getBoundingClientRect()
			appRect = { left: measured?.left ?? 0, top: measured?.top ?? 0 }
		}

		const beginGesture = (
			e: MouseEvent,
			gesturePane: TViewerPane,
			gestureAnchor: { x: number; y: number },
			startsDragging: boolean,
		) => {
			start = { x: e.clientX, y: e.clientY }
			anchor = gestureAnchor
			pane = gesturePane
			isDragging = startsDragging
			latestRect = null
			measureAppRect()
			e.preventDefault()
			e.stopPropagation()
			e.stopImmediatePropagation()
		}

		const onMouseMove = (e: MouseEvent) => {
			if (!start || !anchor) return
			if (!isDragging) {
				if (Math.abs(e.clientX - start.x) < DRAG_THRESHOLD_PX && Math.abs(e.clientY - start.y) < DRAG_THRESHOLD_PX) return
				isDragging = true
			}
			latestRect = {
				left: Math.min(anchor.x, e.clientX),
				top: Math.min(anchor.y, e.clientY),
				right: Math.max(anchor.x, e.clientX),
				bottom: Math.max(anchor.y, e.clientY),
			}
			showDraftRect(latestRect, appRect)
		}

		const onMouseUp = () => {
			if (!start) return
			const rect = isDragging ? latestRect : null
			start = null
			anchor = null
			isDragging = false
			latestRect = null
			if (!rect || rect.right - rect.left < MIN_AREA_SIZE_PX || rect.bottom - rect.top < MIN_AREA_SIZE_PX) {
				syncDraftRect()
				return
			}
			const resolved = AnnotationService.getBackendRectFromClientRect(rect, pane)
			if (!resolved) {
				syncDraftRect()
				return
			}
			setArea({ pageNumber: resolved.pageNumber, coordinates: resolved.coordinates, pane })
		}

		const unbinds: (() => void)[] = []
		for (const binding of panes) {
			const scrollElement = binding.scrollEl
			if (!scrollElement) continue
			const onMouseDown = (e: MouseEvent) => {
				if (e.button !== 0) return
				beginGesture(e, binding.pane, { x: e.clientX, y: e.clientY }, false)
			}
			const onScroll = () => syncDraftRect()
			scrollElement.addEventListener("mousedown", onMouseDown, true)
			scrollElement.addEventListener("scroll", onScroll, { passive: true })
			binding.documentViewer.addEventListener("zoomUpdated", onScroll)
			unbinds.push(() => {
				scrollElement.removeEventListener("mousedown", onMouseDown, true)
				scrollElement.removeEventListener("scroll", onScroll)
				binding.documentViewer.removeEventListener("zoomUpdated", onScroll)
			})
		}

		const draftElement = draftRectRef.current?.element
		for (const [index, handle] of (draftRectRef.current?.handles ?? []).entries()) {
			const onHandleDown = (e: MouseEvent) => {
				if (e.button !== 0) return
				const area = createSessionRef.current?.area
				const box = draftElement?.getBoundingClientRect()
				if (!area || !box) return
				const oppositeX = index === 0 || index === 2 ? box.right : box.left
				const oppositeY = index === 0 || index === 1 ? box.bottom : box.top
				beginGesture(e, area.pane, { x: oppositeX, y: oppositeY }, true)
			}
			handle.addEventListener("mousedown", onHandleDown, true)
			unbinds.push(() => handle.removeEventListener("mousedown", onHandleDown, true))
		}

		window.addEventListener("mousemove", onMouseMove, true)
		window.addEventListener("mouseup", onMouseUp, true)
		return () => {
			for (const unbind of unbinds) unbind()
			window.removeEventListener("mousemove", onMouseMove, true)
			window.removeEventListener("mouseup", onMouseUp, true)
		}
	}, [appContainerRef, createSession, createSessionRef, draftRectRef, getViewerPanes, layoutEpoch, setArea, showDraftRect, syncDraftRect])

	useEffect(() => {
		if (!createSession) return
		syncDraftRect()
	}, [createSession, layoutEpoch, syncDraftRect])

	useEffect(() => () => {
		if (!createSessionRef.current) return
		createSessionRef.current = null
		AnnotationService.endAreaDrawing()
	}, [createSessionRef])

	return { createSession, startCreateArea, cancelCreateArea }
}
